'use client';

import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { ADMIN_TASKS_AREA_NAME_DEFAULT } from '@/lib/admin-tasks';
import { DEFAULT_NON_WORKING_WEEKDAYS } from '@/lib/leave';

interface AppSettings {
  logo_navbar: string | null;
  logo_login: string | null;
  privacy_notice_version: string | null;
  admin_tasks_area_name: string | null;
  toast_duration_ms: number | null;
  daily_holidays: string[];
  daily_non_working_weekdays: number[];
}

const TOAST_DURATION_DEFAULT = 3000;
const PRIVACY_VERSION_DEFAULT = '2026-05-17';

const defaults: AppSettings = {
  logo_navbar: null,
  logo_login: null,
  privacy_notice_version: null,
  admin_tasks_area_name: null,
  toast_duration_ms: null,
  daily_holidays: [],
  daily_non_working_weekdays: Array.from(DEFAULT_NON_WORKING_WEEKDAYS),
};

interface ContextValue {
  settings: AppSettings;
  loaded: boolean;
  refresh: () => Promise<void>;
}

const Ctx = createContext<ContextValue>({
  settings: defaults,
  loaded: false,
  refresh: async () => {},
});

export function AppSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<AppSettings>(defaults);
  const [loaded, setLoaded] = useState(false);

  async function refresh() {
    try {
      const res = await fetch('/api/settings/branding', { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      const dur = Number(data.toast_duration_ms);
      setSettings({
        logo_navbar: data.logo_navbar ?? null,
        logo_login: data.logo_login ?? null,
        privacy_notice_version: data.privacy_notice_version ?? null,
        admin_tasks_area_name: data.admin_tasks_area_name ?? null,
        toast_duration_ms: Number.isFinite(dur) && dur > 0 ? dur : null,
        daily_holidays: Array.isArray(data.daily_holidays) ? data.daily_holidays.map(String) : [],
        daily_non_working_weekdays: Array.isArray(data.daily_non_working_weekdays)
          ? data.daily_non_working_weekdays.map(Number).filter((n: number) => Number.isInteger(n))
          : Array.from(DEFAULT_NON_WORKING_WEEKDAYS),
      });
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => { refresh(); }, []);

  return <Ctx.Provider value={{ settings, loaded, refresh }}>{children}</Ctx.Provider>;
}

export function useAppSettings(): ContextValue {
  return useContext(Ctx);
}

export function useAreaName(): string {
  return useContext(Ctx).settings.admin_tasks_area_name || ADMIN_TASKS_AREA_NAME_DEFAULT;
}

export function useToastDuration(): number {
  return useContext(Ctx).settings.toast_duration_ms || TOAST_DURATION_DEFAULT;
}

export function usePrivacyVersion(): string {
  return useContext(Ctx).settings.privacy_notice_version || PRIVACY_VERSION_DEFAULT;
}

export function useLogo(which: 'navbar' | 'login'): string | null {
  const s = useContext(Ctx).settings;
  return which === 'navbar' ? s.logo_navbar : s.logo_login;
}

export function useWorkingDayConfig(): { holidays: Set<string>; nonWorkingWeekdays: Set<number> } {
  const s = useContext(Ctx).settings;
  return useMemo(() => ({
    holidays: new Set(s.daily_holidays),
    nonWorkingWeekdays: new Set(s.daily_non_working_weekdays),
  }), [s.daily_holidays, s.daily_non_working_weekdays]);
}
